import React, { useMemo, useState } from "react";
import { Container, Card, Carousel, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import useAsync from "../../../hooks/useAsync";
import { typesAPI, retreatsAPI, money, dateRange, nightsLabel } from "../../../services/api";
import "./RetreatCards.css";
import Picture from "../../utils/Picture";

const FALLBACK_IMAGE = "/events/events1.png";

/** What the card shows on the picture: an early-booking saving, or how few places are left on the next date. */
const cardTag = (type) => {
  const next = type.dates[0];
  if (!next) return null;
  if (next.earlyDiscount) return `Early booking · save ${money(next.earlyDiscount.amount)}`;
  if (type.dates.every((d) => d.soldOut)) return "Fully booked";
  if (next.placesLeft <= 3 && !next.soldOut) return `${next.placesLeft} ${next.placesLeft === 1 ? "place" : "places"} left`;
  return null;
};

/**
 * The cards are retreat TYPES — Ayahuasca, Women's, Veterans… — each with
 * however many dates are open. Before the database has types (the v2
 * migration not yet run) every dated retreat stands in as its own type,
 * so the homepage never goes blank during a rollout.
 */
const loadTypes = () =>
  typesAPI.list().catch(() =>
    retreatsAPI.list().then((rows) => rows.map((r) => ({
      id: `p${r.id}`, slug: r.slug, name: r.title, tagline: r.tagline, description: r.description,
      tags: r.focus || [], cardImage: r.image,
      dates: [{ id: r.id, slug: r.slug, startDate: r.startDate, endDate: r.endDate, nights: r.nights, priceFrom: r.price, placesLeft: r.placesLeft, soldOut: r.soldOut }],
    }))));

/* The two ways out of this section, under the carousel on every size.
   They're the only links there, so they carry their own arrow rather
   than a button that would compete with RESERVE on the cards. */
const RetreatLinks = ({ dark }) => (
  <div className={`retreat-links ${dark ? "retreat-links--dark" : ""}`}>
    <Link to="/contact" className="retreat-link pFont">
      SET UP A<span className="d-block">DISCOVERY CALL</span>
    </Link>
    <Link to="/retreats" className="retreat-link retreat-link--end pFont">
      FIND OUT<span className="d-block">MORE</span>
    </Link>
  </div>
);

/* The carousel chevrons, shared by all three breakpoints — the tablet and
   phone carousels used to fall back to Bootstrap's own icons, which this
   stylesheet hides, so they had no arrows at all. */
const prevIcon = (
  <span className="custom-carousel-arrow prev-arrow">
    <img src="/arrowLeft.svg" alt="" />
  </span>
);
const nextIcon = (
  <span className="custom-carousel-arrow next-arrow">
    <img src="/arrowLeft.svg" alt="" style={{ transform: "rotateY(180deg)" }} />
  </span>
);

/* The cards used to come in from 500px to the right, which on anything
   narrower than a desktop parked them outside the carousel — and the
   carousel clips what overflows it. A card nobody can see never counts
   as "in view", so it never animated in: on a tablet the second card
   stayed at opacity 0 for ever, waiting for itself. Sixty pixels reads
   as the same movement and always starts somewhere visible. */
const CARD_VIEWPORT = { once: true, amount: 0.15 };

const cardVariants = {
  hidden: { opacity: 0, x: 60 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
  }),
};

// Helper to chunk data into groups of n
const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

/**
 * The retreat-type carousel, used on the home, about and resort pages, and
 * again at the bottom of a retreat page as "other retreats".
 *
 * exclude — a type slug to leave out (the one you're already looking at)
 * heading — title above the carousel
 * dark    — sit on the dark background instead of the light one
 */
const RetreatCards = ({ exclude = null, heading = null, dark = false }) => {
  const { data: types, loading } = useAsync(loadTypes, [], []);
  const items = (types || []).filter((t) => t.slug !== exclude);

  const desktopChunks = useMemo(() => chunkArray(items, 3), [items]);
  const tabletChunks = useMemo(() => chunkArray(items, 2), [items]);

  if (loading || items.length === 0) {
    return (
      <div className={`ch-100 ${dark ? "grain-bg" : "grain-bg-light"} d-flex align-items-center justify-content-center px-lg-5`}>
        <p className={`fs-4 mb-0 ${dark ? "text-secondary-color" : "text-primary-color"}`}>
          {loading ? "Loading retreats…" : "New dates are on the way. Join the newsletter below and we'll let you know."}
        </p>
      </div>
    );
  }

  return (
    <div className={`ch-100 ${dark ? "grain-bg" : "grain-bg-light"} d-flex align-items-center px-lg-5`}>
      <Container fluid className="py-5 px-5">
        {heading && <p className={`fs-5 text-center pFont fw-bold wSpacing mb-4 ${dark ? "text-secondary-color" : "text-primary-color"}`}>{heading}</p>}
        {/* DESKTOP (>= 992px) */}
        <div className="d-none d-lg-block">
          <Carousel interval={null} indicators={false} controls={desktopChunks.length > 1} prevIcon={prevIcon} nextIcon={nextIcon}>
            {desktopChunks.map((group, index) => (
              <Carousel.Item key={index}>
                <Row className="g-4 justify-content-center">
                  {group.map((item, i) => (
                    <Col lg={4} key={item.id} className="d-flex">
                      <TypeCard type={item} i={i} />
                    </Col>
                  ))}
                </Row>
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* TABLETS (≥768px and <992px) */}
        <div className="d-none d-md-block d-lg-none">
          <Carousel interval={null} indicators={false} controls={tabletChunks.length > 1} prevIcon={prevIcon} nextIcon={nextIcon}>
            {tabletChunks.map((group, index) => (
              <Carousel.Item key={index}>
                <Row className="g-4 justify-content-center">
                  {group.map((item, i) => (
                    <Col md={6} key={item.id} className="d-flex">
                      <TypeCard type={item} i={i} small />
                    </Col>
                  ))}
                </Row>
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* MOBILE (<768px) */}
        <div className="d-block d-md-none">
          <Carousel interval={null} indicators={false} controls={items.length > 1} prevIcon={prevIcon} nextIcon={nextIcon}>
            {items.map((item, index) => (
              <Carousel.Item key={item.id}>
                <TypeCard type={item} i={index} small />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        <RetreatLinks dark={dark} />
      </Container>
    </div>
  );
};

/**
 * One retreat type. "See dates" opens the dates for this type inside the
 * card; picking one goes to the retreat page with that date chosen.
 * "Explore" goes to the retreat page.
 */
const TypeCard = ({ type, i, small = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const next = type.dates[0];
  const tag = cardTag(type);
  const listId = `dates-${type.id}`;

  return (
    <motion.div custom={i} variants={cardVariants} initial="hidden" whileInView="visible" viewport={CARD_VIEWPORT} className="w-100 h-100">
      <Card className="retreat-card shadow-sm text-center rounded-0 h-100 grain-bg-light">
        <div className="retreat-img-wrapper">
          {tag && <div className="retreat-tag px-3 py-1 fs-6">{tag}</div>}
          <Picture src={type.cardImage || FALLBACK_IMAGE} className="retreat-img card-img" sizes="(min-width: 992px) 30vw, (min-width: 768px) 45vw, 90vw" width={600} height={420} />
        </div>

        <Card.Body className="mt-3 d-flex flex-column pb-5">
          <h3 className={`${small ? "fs-3" : "fs-2"} text-primary-color mb-2`}>{type.name}</h3>
          <p className="pFont text-primary-color mb-3 type-next">
            {next
              ? <>{dateRange(next.startDate, next.endDate)} · {nightsLabel(next.nights)} · from {money(next.priceFrom ?? next.price)}</>
              : "New dates coming soon"}
          </p>
          <p className={`retreat-desc ${small ? "fs-5" : "fs-4"} mb-4 text-primary-color`}>{type.tagline || type.description}</p>

          <div className="mt-auto">
            {type.tags?.length > 0 && (
              <div className="retreat-tags-container mb-4">
                {type.tags.map((t) => <span className="focus-tag text-primary-color fs-6" key={t}>{t}</span>)}
              </div>
            )}

            {open && (
              <ul className="type-dates" id={listId}>
                {type.dates.map((d) => (
                  <li key={d.id}>
                    <Link to={`/retreats/${type.slug}?date=${d.id}`} className="type-date">
                      <span className="type-date-when">{dateRange(d.startDate, d.endDate)}</span>
                      <span className="type-date-meta">
                        {d.soldOut ? "Full · waitlist" : `${d.placesLeft} ${d.placesLeft === 1 ? "space" : "spaces"} left`}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <div className="type-actions">
              {type.dates.length > 0 ? (
                <button type="button" className="type-toggle pFont" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((o) => !o)}>
                  {open ? "Hide dates" : `See ${type.dates.length === 1 ? "date" : `${type.dates.length} dates`}`}
                </button>
              ) : (
                <Link className="type-toggle pFont" to={`/retreats/${type.slug}#notify`}>Tell me when</Link>
              )}
              <button className="blob-btn px-5 fs-4 py-3" onClick={() => navigate(`/retreats/${type.slug}`)}>
                EXPLORE
              </button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </motion.div>
  );
};

export default RetreatCards;
